import { useRuntimeConfigStore } from '@/core/config/runtime-config.loader';

import type { ApiError } from './api-error';
import { handleAuthError } from './auth-error-handler';
import { normalizeError } from './error-normalize';

type NavigateFn = (path: string) => void;

let navigateCallback: NavigateFn = (path) => {
  globalThis.location.assign(path);
};

export function setNavigate(fn: NavigateFn): void {
  navigateCallback = fn;
}

export interface RequestOptions extends Omit<RequestInit, 'body' | 'method'> {
  body?: unknown;
  skipAuthRedirect?: boolean;
}

type BlobRequestOptions = Omit<RequestOptions, 'body'>;

async function request<T>(
  url: string,
  method: string,
  { skipAuthRedirect, ...options }: RequestOptions = {},
): Promise<T> {
  const init = buildInit(url, method, options);

  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (err) {
    if (isAbort(err)) throw err;
    throw normalizeError(err);
  }

  if (!response.ok) {
    throw await buildErrorFromResponse(response, skipAuthRedirect);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('Content-Type') ?? '';
  if (contentType.includes('application/json')) {
    return (await response.json()) as T;
  }

  const text = await response.text();
  if (!text) {
    return undefined as T;
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    return text as unknown as T;
  }
}

async function requestBlob(
  url: string,
  method: string,
  { skipAuthRedirect, ...options }: RequestOptions = {},
): Promise<Blob> {
  const init = buildInit(url, method, options);

  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (err) {
    if (isAbort(err)) throw err;
    throw normalizeError(err);
  }

  if (!response.ok) {
    throw await buildErrorFromResponse(response, skipAuthRedirect);
  }

  return response.blob();
}

function buildInit(url: string, method: string, options: RequestOptions): RequestInit {
  const { body, headers: headersInit, credentials, ...rest } = options;
  const normalizedMethod = method.toUpperCase();
  const config = useRuntimeConfigStore.getState().config;
  const isAppRequest =
    config !== null &&
    (url.startsWith(config.baseUrl) || url.startsWith(config.authBaseUrl));

  const headers = new Headers(headersInit);
  const bodyIsFormData = body instanceof FormData;
  const bodyIsBlob = body instanceof Blob;

  if (
    !headers.has('Content-Type') &&
    body !== undefined &&
    body !== null &&
    !bodyIsFormData &&
    !bodyIsBlob
  ) {
    headers.set('Content-Type', 'application/json');
  }
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }

  if (isAppRequest && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(normalizedMethod)) {
    const xsrf = readCookie('XSRF-TOKEN');
    if (xsrf) {
      headers.set('X-XSRF-TOKEN', xsrf);
    }
  }

  return {
    ...rest,
    method: normalizedMethod,
    headers,
    credentials: isAppRequest ? 'include' : credentials ?? 'same-origin',
    body: serializeBody(body),
  };
}

async function buildErrorFromResponse(
  response: Response,
  skipAuthRedirect = false,
): Promise<ApiError> {
  const raw = await safeParseBody(response);
  const normalized = normalizeError({
    status: response.status,
    statusText: response.statusText,
    error: raw,
  });
  if (!skipAuthRedirect) {
    handleAuthError(normalized, navigateCallback);
  }
  return normalized;
}

function isAbort(err: unknown): err is DOMException {
  return err instanceof DOMException && err.name === 'AbortError';
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const pattern = new RegExp('(?:^|; )' + escapeRegex(name) + '=([^;]*)');
  const match = pattern.exec(document.cookie);
  return match ? decodeURIComponent(match[1]) : null;
}

function escapeRegex(value: string): string {
  return value.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}

function serializeBody(body: unknown): BodyInit | null | undefined {
  if (body === undefined || body === null) return null;
  if (body instanceof FormData || body instanceof Blob || body instanceof URLSearchParams) {
    return body;
  }
  if (body instanceof ArrayBuffer) {
    return body;
  }
  if (typeof body === 'string') {
    return body;
  }
  return JSON.stringify(body);
}

async function safeParseBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('Content-Type') ?? '';
  if (contentType.includes('application/json')) {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }

  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export function apiGet<T>(url: string, options?: RequestOptions): Promise<T> {
  return request<T>(url, 'GET', options);
}

export function apiPost<T>(url: string, body?: unknown, options?: RequestOptions): Promise<T> {
  return request<T>(url, 'POST', { ...options, body });
}

export function apiPostMultipart<T>(url: string, form: FormData, options?: RequestOptions): Promise<T> {
  return request<T>(url, 'POST', { ...options, body: form });
}

export function apiPut<T>(url: string, body?: unknown, options?: RequestOptions): Promise<T> {
  return request<T>(url, 'PUT', { ...options, body });
}

export function apiPutMultipart<T>(url: string, form: FormData, options?: RequestOptions): Promise<T> {
  return request<T>(url, 'PUT', { ...options, body: form });
}

export function apiPatch<T>(url: string, body?: unknown, options?: RequestOptions): Promise<T> {
  return request<T>(url, 'PATCH', { ...options, body });
}

export function apiPatchMultipart<T>(url: string, form: FormData, options?: RequestOptions): Promise<T> {
  return request<T>(url, 'PATCH', { ...options, body: form });
}

export function apiDelete<T>(url: string, options?: RequestOptions): Promise<T> {
  return request<T>(url, 'DELETE', options);
}

export function apiGetBlob(url: string, options?: BlobRequestOptions): Promise<Blob> {
  return requestBlob(url, 'GET', options);
}

export function apiPostBlob(url: string, body?: unknown, options?: RequestOptions): Promise<Blob> {
  return requestBlob(url, 'POST', { ...options, body });
}
